import React, { FormEvent, useCallback, useContext, useEffect, useState } from 'react';
import moment from 'moment';
import { useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import Button from 'src/components/controls/forms/redesign/FormButton';
import SurfaceWaiver, { SurfaceWaiverHold } from 'src/models/contract_types/SurfaceWaiver';
import { useFetch } from 'use-http';
import { push, replace } from 'connected-react-router';
import { IAppContextState } from 'src/reducers/AppContext';
import { AppContext } from 'src/providers/AppContextProvider';
import RHFLegend from 'src/components/controls/tempRHFControls/RHFLegend';
import RHFInputBox from 'src/components/controls/tempRHFControls/RHFInputBox';
import RHFSelect from 'src/components/controls/tempRHFControls/RHFSelect';
import IndexReferenceType from 'src/models/IndexReferenceType';
import RHFCheckbox from 'src/components/controls/tempRHFControls/RHFCheckBox';
import User from 'src/models/User';
import actions from 'src/actions';
import DialogList from 'src/dialogs';
import ContractHoldDocs from '../../LeasingComponents/redesign/ContractHoldDocs';
import { DialogContext, dialogCreators } from 'src/providers/DialogProvider';
import PropertyUtil from 'src/util/PropertyUtil';
import WIOwnership from 'src/models/tract_detail/WIOwnership';
import Owner from 'src/models/tract_detail/Owner';
import Ownership from 'src/models/tract_detail/Ownership';
import AddWIOwnershipData from 'src/models/tract_detail/add_ownership_revised/AddWIOwnership';
import ContractWorkingInterestList from '../../LeasingComponents/redesign/ContractWorkingInterestList';
import WIContractOwnership from 'src/models/tract_detail/WIContractOwnership';
import RHFTextArea from 'src/components/controls/tempRHFControls/RHFTextArea';
// import MathUtil from 'src/util/MathUtil';
import OwnerUtil from 'src/util/OwnerUtil';
import AddOwnershipPages from '../AddOwnershipPages';
import RHFToggle from 'src/components/controls/tempRHFControls/RHFToggle';
import AgreementUtil from 'src/util/AgreementUtil';
import ProvisionLeaseClause from 'src/models/ProvisionLeaseClause';
import ProvisionClauseType from 'src/models/ProvisionClauseType';
import LandContractType from 'src/models/LandContractType';
import ProvisionClause from 'src/components/common/ProvisionClause';

const SurfaceWaiverInfo = () => {
    const { register, handleSubmit, reset, errors, formState, getValues, setValue, watch } = useForm();
    const { del, get, put, post, response } = useFetch();
    const { isDirty } = formState;

    const appContext = useContext<IAppContextState>(AppContext);
    const dispatch = useDispatch();
    const dialogContext = React.useContext<dialogCreators>(DialogContext);
    const watchExpirationCalcFields = watch(['effectiveDate', 'currentHold.primaryTerm', 'currentHold.primaryTermTypeId']);

    const [surfaceWaiverData, setSurfaceWaiverData] = useState<SurfaceWaiver>(undefined);
    const [provisionClauseTypes, setProvisionClauseTypes] = useState<Array<ProvisionClauseType>>(undefined);
    const [attachedProvisions, setAttachedProvisions] = useState<Array<ProvisionLeaseClause>>(undefined);
    const [measurementTypes, setMeasurementTypes] = useState<Array<IndexReferenceType>>([]);
    const [users, setUsers] = useState<User[]>([]);
    const [currentWaiverHold, setCurrentWaiverHold] = useState<number>(0);
    const [isPerpetual, setIsPerpetual] = useState<boolean>(false);
    const [selectedWIOwnershipId, setSelectedWIOwnershipId] = useState<number>(0);
    const [royalty, setRoyalty] = useState(0);
    const [copyLeaseholdClicked, setCopyLeaseholdClicked] = useState<boolean>(false);
    const [sourceSelected, setSourceSelected] = useState<boolean>(false);

    useEffect(() => {
        console.log('Surface Waiver Page loaded');
        getMeasurementTypes();
        getUsers();
        getProvisionClauseTypes();
    }, []);

    useEffect(() => {
        if (surfaceWaiverData?.id > 0) {
            reset(prepData(surfaceWaiverData));
        }
    }, [surfaceWaiverData, currentWaiverHold]);

    useEffect(() => {
        if (appContext.surfaceWaiverId > 0 && users.length > 0 && measurementTypes.length > 0) {
            getSurfaceWaiverData(appContext.surfaceWaiverId);
        }
    }, [users, measurementTypes]);

    useEffect(() => {
        if (surfaceWaiverData?.id > 0) {
            if (
                appContext.surfaceWaiverHoldId > 0 &&
                surfaceWaiverData?.surfaceWaiverHolds?.findIndex((swh) => swh.id === appContext.surfaceWaiverHoldId) > -1
            ) {
                const waiverHoldIndex = surfaceWaiverData?.surfaceWaiverHolds?.findIndex((swh) => swh.id === appContext.surfaceWaiverHoldId);
                setCurrentWaiverHold(waiverHoldIndex);
            } else if (appContext.surfaceWaiverHoldId === 0) {
                const tempSurfaceWaiverData = surfaceWaiverData;
                tempSurfaceWaiverData.surfaceWaiverHolds.push(new SurfaceWaiverHold());
                setSurfaceWaiverData(tempSurfaceWaiverData);
                setCurrentWaiverHold(tempSurfaceWaiverData?.surfaceWaiverHolds?.length - 1);
                setRoyalty(0);
                setAttachedProvisions([]);
            }
        }
    }, [appContext.surfaceWaiverHoldId, surfaceWaiverData, reset]);

    // Enable Copy Leasehold button when the selected Source changes from default
    const handleSourceSelected = (e: FormEvent<HTMLSelectElement>) => {
        const selectedItem = e.target as HTMLSelectElement;

        if (selectedItem.value !== '-- Select --') {
            setSourceSelected(true);
            setCopyLeaseholdClicked(true);
        }
    };            

    const prepData = useCallback(
        (roughWaiverData: SurfaceWaiver) => {
            console.log('Rough Waiver Data:', roughWaiverData);

            const formData: SurfaceWaiver = {
                id: roughWaiverData.id,
                surfaceWaiverNumber: roughWaiverData.surfaceWaiverNumber,
                name: roughWaiverData.name,
                createdDate: roughWaiverData.createdDate ? (moment(roughWaiverData.createdDate).format('YYYY-MM-DD') as unknown as Date) : null,
                createdByUserId: roughWaiverData.createdByUserId,
                leasedByUserId: roughWaiverData.leasedByUserId,
                leasedBy: roughWaiverData.leasedBy,
                originalLessee: roughWaiverData.originalLessee,
                lesseeName: roughWaiverData.lesseeName,
                lesseeAddressLine1: roughWaiverData.lesseeAddressLine1,
                lesseeAddressLine2: roughWaiverData.lesseeAddressLine2,
                lesseeCity: roughWaiverData.lesseeCity,
                lesseeState: roughWaiverData.lesseeState,
                lesseeZipCode: roughWaiverData.lesseeZipCode,
                lesseePhoneNumber: roughWaiverData.lesseePhoneNumber,
                isNotOriginalLessee: roughWaiverData.isNotOriginalLessee,
                grantorAka: roughWaiverData.grantorAka,
                spouseName: roughWaiverData.spouseName,
                spouseAkaName: roughWaiverData.spouseAkaName,
                maritalStatus: roughWaiverData.maritalStatus,
                leaseLessorNotes: roughWaiverData.leaseLessorNotes,
                fedStateLeaseNumber: roughWaiverData.fedStateLeaseNumber,
                legalDescription: roughWaiverData.legalDescription,
                rteLegalDescription: roughWaiverData.rteLegalDescription,
                grossAcres: roughWaiverData.grossAcres,
                recordDate: roughWaiverData.recordDate ? (moment(roughWaiverData.recordDate).format('YYYY-MM-DD') as unknown as Date) : null,
                recordTypeId: roughWaiverData.recordTypeId,
                volume: roughWaiverData.volume,
                page: roughWaiverData.page,
                instrumentNumber: roughWaiverData.instrumentNumber,
                isVoid: roughWaiverData.isVoid,
                projectId: roughWaiverData.projectId,
                isFiledByMemo: roughWaiverData.isFiledByMemo,
                inHandDate: roughWaiverData.inHandDate ? (new Date(roughWaiverData.inHandDate).toISOString().split('T')[0] as unknown as Date) : null,
                originalInHandDate: roughWaiverData.originalInHandDate ? (new Date(roughWaiverData.originalInHandDate).toISOString().split('T')[0] as unknown as Date) : null,
                commitDate: roughWaiverData.commitDate ? (new Date(roughWaiverData.commitDate).toISOString().split('T')[0] as unknown as Date) : null,
                voidDate: roughWaiverData.voidDate ? (new Date(roughWaiverData.voidDate).toISOString().split('T')[0] as unknown as Date) : null,
                reminderDate: roughWaiverData.reminderDate ? (new Date(roughWaiverData.reminderDate).toISOString().split('T')[0] as unknown as Date) : null,
                recordedDate: roughWaiverData.recordedDate ? (new Date(roughWaiverData.recordedDate).toISOString().split('T')[0] as unknown as Date) : null,
                contractDate: roughWaiverData.contractDate
                    ? (new Date(roughWaiverData.contractDate).toISOString().split('T')[0] as unknown as Date)
                    : null,
                effectiveDate: roughWaiverData.effectiveDate
                    ? (new Date(roughWaiverData.effectiveDate).toISOString().split('T')[0] as unknown as Date)
                    : null,
                clientDocNumber: roughWaiverData.clientDocNumber,
                reminderNote: roughWaiverData.reminderNote,
                ownersOnDocument: roughWaiverData.ownersOnDocument,
                ownersAddressOnDocument: roughWaiverData.ownersAddressOnDocument,    
                ownersSSNTaxIdOnDocument: roughWaiverData.ownersSSNTaxIdOnDocument,
                
                currentHold: {
                    ...roughWaiverData.surfaceWaiverHolds?.[currentWaiverHold],
                    expirationDate: roughWaiverData.surfaceWaiverHolds?.[currentWaiverHold]?.expirationDate
                    ? (new Date(roughWaiverData.surfaceWaiverHolds?.[currentWaiverHold]?.expirationDate)
                    .toISOString()
                    .split('T')[0] as unknown as Date)
                    : null,
                    waiverDate: roughWaiverData.surfaceWaiverHolds?.[currentWaiverHold]?.waiverDate
                    ? (new Date(roughWaiverData.surfaceWaiverHolds?.[currentWaiverHold]?.waiverDate)
                    .toISOString()
                    .split('T')[0] as unknown as Date)
                    : null,
                    isActive: roughWaiverData.surfaceWaiverHolds?.[currentWaiverHold]?.isActive,
                    provisionLeaseClauses: roughWaiverData.surfaceWaiverHolds?.[currentWaiverHold]?.provisionLeaseClauses,
                },
            };

            // setup all watches here
            setIsPerpetual(formData.currentHold.isPerpetual);

            if (formData.currentHold.provisionLeaseClauses?.length > 0) {
                for (let i = 0; i < formData.currentHold.provisionLeaseClauses.length; i++) {
                    formData.currentHold.provisionLeaseClauses[i].obligationDate = formData.currentHold.provisionLeaseClauses[i].obligationDate
                        ? (moment(formData.currentHold.provisionLeaseClauses[i].obligationDate).format('YYYY-MM-DD') as unknown as Date)
                        : null;
                    formData.currentHold.provisionLeaseClauses[i].obligationEndDate = formData.currentHold.provisionLeaseClauses[i].obligationEndDate
                        ? (moment(formData.currentHold.provisionLeaseClauses[i].obligationEndDate).format('YYYY-MM-DD') as unknown as Date)
                        : null;
                }
                setAttachedProvisions(formData.currentHold.provisionLeaseClauses);
            }

            return formData;
        },
        [currentWaiverHold]
    );

    const getSurfaceWaiverData = async (surfaceWaiverId: number) => {
        const result: SurfaceWaiver = await get(
            `/v1.0/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/ownership/${appContext.ownershipId}/surfaceWaiver/${surfaceWaiverId}`
        );
        if (response.ok) {
            console.log('Waiver Result:', result);
            setSurfaceWaiverData(finalizeSurfaceWaiver(result));
        } else {
            window.alert('Get Surface Waiver Data failed');
        }
    };

    const getMeasurementTypes = async () => {
        const result = await get(`/v1.0/referenceTypes`);

        if (response.ok) {
            setMeasurementTypes(result);
        }
    };

    const getProvisionClauseTypes = async () => {
        const result: Array<ProvisionClauseType> = await get(
            `/v1.0/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/provisionLeaseClause/types/${LandContractType.SurfaceWaiver}`
        );
        if (response.ok && Array.isArray(result)) {
            setProvisionClauseTypes(result ?? []);
        }
    };

    const updateSurfaceWaiver = async (data: SurfaceWaiver) => {
        await put(
            `/v1.0/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/ownership/${appContext.ownershipId}/surfaceWaiver`,
            data
        );

        if (response.ok) {
            dispatch(
                push(window.location.pathname)
            );

            getSurfaceWaiverData(appContext.surfaceWaiverId);
        } else {
            window.alert('Update Surface Waiver failed');
        }
    };

    const deleteSurfaceWaiverhold = (deleteParent: boolean) => {
        if (surfaceWaiverData?.isGeneratedLease === true && surfaceWaiverData?.isInHand === true) {
            window.alert('Cannot delete a generated Agreement. Please review and update as necessary in the Agreements tab.');
            return;
        }
        const confirmAction = async () => {
            const deleteId = surfaceWaiverData?.surfaceWaiverHolds?.[currentWaiverHold].id;
            let currentPath = window.location.pathname.split('/');
            let spliceLength = deleteParent === true ? 5 : 1;
            currentPath.splice(currentPath.length - spliceLength);
            
            // tslint:disable-next-line: max-line-length
            await del(`/v1.0/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/ownership/${appContext.ownershipId}/surfaceWaiver/${deleteId}/${deleteParent}`);
            if (response.ok) {
                if (!deleteParent) {
                    const switchToWaiverholdId = surfaceWaiverData?.surfaceWaiverHolds.find((x) => x.id !== surfaceWaiverData?.surfaceWaiverHolds?.[0]?.id)?.id;
                    getSurfaceWaiverData(appContext.surfaceWaiverId);
                    dispatch(
                        replace(
                            `${currentPath.join('/')}/${switchToWaiverholdId}`
                        )
                    );
                } else {
                    dispatch(
                        replace(
                            `${currentPath.join('/')}`
                        )
                    );
                }
            
            } else {
                window.alert('delete waiverhold failed');
            }
        };

        let warning = deleteParent
            ? 'This is the only leasehold. The lease will also be deleted. Continue?'
            : 'Are you sure you want to delete this Leasehold?';

        const body = (
            <>
                <div style={{ fontWeight: 'bold' }}>{warning}</div>
            </>
        );

        dialogContext.common.confirmAction(`Delete Leasehold`, body, confirmAction);
    };

    const createSurfaceWaiver = async (data: SurfaceWaiver) => {
        let currentPath = window.location.pathname.split('/');
        currentPath.splice(currentPath.length - 3, 3);

        let result = await post(
            `/v1.0/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/ownership/${appContext.ownershipId}/surfaceWaiver`,
            data
        );
        if (response.ok) {
            dispatch(
                replace(
                    `${currentPath.join('/')}/${result.contractId}/surface-waiver-holds/${result.contractHoldId}`
                )
            );
            getSurfaceWaiverData(result.contractId);
        } else {
            window.alert('Create Surface Waiver failed');
        }
    };

    const onCopyLeasehold = async () => {
        let copyLeaseholdId = getValues('sourceLeaseholdId');

        // tslint:disable-next-line: max-line-length
        const result = await put(`/v1.0/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/ownership/${appContext.ownershipId}/surfaceWaiver/surface-waiver-hold/${appContext.surfaceWaiverHoldId}/copy-leasehold/${copyLeaseholdId}`);

        if (response.ok) {
            let currentPath = window.location.pathname.split('/');
            currentPath.splice(currentPath.length - 1, 1);
    
            dispatch(
                push(`${currentPath.join('/')}/${result}`)
            );
            getSurfaceWaiverData(appContext.surfaceWaiverId);
        } else {
            window.alert('call failed');
        }
    };

    const leaseholdList = () => {
        if (surfaceWaiverData?.surfaceWaiverHolds?.length > 1) {
            let holds = [...surfaceWaiverData.surfaceWaiverHolds];
            holds.pop();
            let leaseholdIds = holds.map((lh: SurfaceWaiverHold) => {
                return { value: lh.id || 0, label: lh.surfaceWaiverHoldNumber?.toString() || 'Error' };
            });

            return leaseholdIds;
        } else {
            return [];
        }
    };

    const onSubmit = (data: SurfaceWaiver) => {
        data.surfaceWaiverHolds = new Array<SurfaceWaiverHold>(data.currentHold);
        data = finalizeSurfaceWaiver(data);
        // data.id = leaseData.id;
        if (data.id > 0) {
            updateSurfaceWaiver(data);
        } else {
            data.isInHand = true;
            data.inHandDate = new Date();
            data.originalInHandDate = new Date();
            data.isCommitted = true;
            data.commitDate = new Date();
            createSurfaceWaiver(data);
        }
    };

    const finalizeSurfaceWaiver = (data: SurfaceWaiver) => {
        data.id = data.id ? parseInt(data.id + '', 10) : 0;
        data.surfaceWaiverNumber = surfaceWaiverData?.surfaceWaiverNumber ? surfaceWaiverData?.surfaceWaiverNumber : 0;
        data.rteLegalDescription = surfaceWaiverData?.rteLegalDescription ? surfaceWaiverData.rteLegalDescription : '';

        data.createdByUserId = data.createdByUserId ? parseInt(data.createdByUserId + '', 10) : null;
        data.leasedByUserId = data.leasedByUserId ? parseInt(data.leasedByUserId + '', 10) : null;
        data.recordTypeId = data.recordTypeId ? parseInt(data.recordTypeId + '', 10) : null;
        
        data.grossAcres = data.grossAcres ? parseFloat(data.grossAcres + '') : 0;
        
        data.name = data.name ? data.name : '';
        data.grantorAka = data.grantorAka ? data.grantorAka : '';
        data.spouseName = data.spouseName ? data.spouseName : '';
        data.spouseAkaName = data.spouseAkaName ? data.spouseAkaName : '';
        data.maritalStatus = data.maritalStatus ? data.maritalStatus : '';
        data.leaseLessorNotes = data.leaseLessorNotes ? data.leaseLessorNotes : '';
        data.fedStateLeaseNumber = data.fedStateLeaseNumber ? data.fedStateLeaseNumber : '';
        data.legalDescription = data.legalDescription ? data.legalDescription : '';
        data.volume = data.volume ? data.volume : '';
        data.page = data.page ? data.page : '';
        data.instrumentNumber = data.instrumentNumber ? data.instrumentNumber : '';
        data.ownersOnDocument = data.ownersOnDocument ? data.ownersOnDocument : '';
        data.lesseeName = data.lesseeName ? data.lesseeName : '';

        data.isFiledByMemo = data.isFiledByMemo ? data.isFiledByMemo : false;
        
        data.createdDate = data.createdDate
        ? (moment(data.createdDate).format('YYYY-MM-DD') as unknown as Date)
        : (moment().format('YYYY-MM-DD') as unknown as Date);
        data.recordedDate = data.recordedDate ? (moment(data.recordedDate).format('YYYY-MM-DD') as unknown as Date) : null;
        data.recordDate = data.recordDate ? (moment(data.recordDate).format('YYYY-MM-DD') as unknown as Date) : null;
        data.contractDate = data.contractDate ? (moment(data.contractDate).format('YYYY-MM-DD') as unknown as Date) : null;
        data.effectiveDate = data.effectiveDate ? (moment(data.effectiveDate).format('YYYY-MM-DD') as unknown as Date) : null;
        
        if (!!surfaceWaiverData) {
            data.reminderNote = surfaceWaiverData?.reminderNote ? surfaceWaiverData.reminderNote : '';
            data.lesseeAddressLine1 = surfaceWaiverData?.lesseeAddressLine1 ? surfaceWaiverData.lesseeAddressLine1 : '';
            data.lesseeAddressLine2 = surfaceWaiverData?.lesseeAddressLine2 ? surfaceWaiverData.lesseeAddressLine2 : '';
            data.lesseeCity  = surfaceWaiverData?.lesseeCity ? surfaceWaiverData.lesseeCity : '';
            data.lesseeState = surfaceWaiverData?.lesseeState ? surfaceWaiverData.lesseeState : '';
            data.lesseeZipCode = surfaceWaiverData?.lesseeZipCode ? surfaceWaiverData.lesseeZipCode : '';
            data.ownersAddressOnDocument = surfaceWaiverData?.ownersAddressOnDocument ? surfaceWaiverData.ownersAddressOnDocument : '';
    
            data.isNotOriginalLessee = surfaceWaiverData?.isNotOriginalLessee ? surfaceWaiverData.isNotOriginalLessee : false;
            data.isGeneratedLease = surfaceWaiverData?.isGeneratedLease ? surfaceWaiverData.isGeneratedLease : false;
            data.isVoid = surfaceWaiverData?.isVoid ? surfaceWaiverData.isVoid : false;
            data.isNotOriginalLessee = surfaceWaiverData?.isNotOriginalLessee ? surfaceWaiverData.isNotOriginalLessee : false;
            data.isActive = data.currentHold?.isActive ? true : false;
            
            data.voidDate = surfaceWaiverData?.voidDate ? (moment(surfaceWaiverData.voidDate).format('YYYY-MM-DD') as unknown as Date) : null;
            data.commitDate = surfaceWaiverData?.commitDate ? (moment(surfaceWaiverData.commitDate).format('YYYY-MM-DD') as unknown as Date) : null;
            data.inHandDate = surfaceWaiverData?.inHandDate ? (moment(surfaceWaiverData.inHandDate).format('YYYY-MM-DD') as unknown as Date) : null;
            data.originalInHandDate = surfaceWaiverData?.originalInHandDate ? (moment(surfaceWaiverData.originalInHandDate).format('YYYY-MM-DD') as unknown as Date) : null;
            data.reminderDate = surfaceWaiverData?.reminderDate ? (moment(surfaceWaiverData.reminderDate).format('YYYY-MM-DD') as unknown as Date) : null;
        }

        if (data.surfaceWaiverHolds?.length > 0) {
            let formatted = new Array<SurfaceWaiverHold>();
            data.surfaceWaiverHolds.forEach((surfaceWaiverHold: SurfaceWaiverHold) => {
                if (formatted.length === currentWaiverHold && !!data.currentHold) {
                    formatted.push(finalizeSurfaceWaiverHold(data.currentHold, data.grossAcres));
                } else {
                    formatted.push(finalizeSurfaceWaiverHold(surfaceWaiverHold, data.grossAcres));
                }
                if (surfaceWaiverHold.provisionLeaseClauses?.length > 0) {
                    surfaceWaiverHold.provisionLeaseClauses = surfaceWaiverHold.provisionLeaseClauses.map((plc) => {
                        plc.invoiceableLookupId = data.invoiceableLookupId;
                        plc.contractLookupId = surfaceWaiverData?.surfaceWaiverHolds?.[currentWaiverHold]?.contractLookupId ?? null;
                        plc.id = plc.id ? parseInt(plc.id + '', 10) : 0;
                        plc.contractAdHocClauseId = plc.contractAdHocClauseId ? parseInt(plc.contractAdHocClauseId + '', 10) : null;
                        plc.leaseAdHocClauseId = plc.leaseAdHocClauseId ? parseInt(plc.leaseAdHocClauseId + '', 10) : null;
                        plc.contractAddendumClauseId = plc.contractAddendumClauseId ? parseInt(plc.contractAddendumClauseId + '', 10) : null;
                        plc.clauseTypeId = plc.clauseTypeId ? parseInt(plc.clauseTypeId + '', 10) : null;
                        plc.frequency = plc.frequency ? parseInt(plc.frequency + '', 10) : null;
                        plc.obligationDate = plc.obligationDate ? (moment(plc.obligationDate).format('YYYY-MM-DD') as unknown as Date) : null;
                        plc.obligationEndDate = plc.obligationEndDate ? (moment(plc.obligationEndDate).format('YYYY-MM-DD') as unknown as Date) : null;
                        plc.useClientApproved = plc.useClientApproved ? AgreementUtil.parseBool(plc.useClientApproved + '') : null;
                        return plc;
                    });
                } else {
                    surfaceWaiverHold.provisionLeaseClauses = surfaceWaiverHold.provisionLeaseClauses ?? [];
                }
            });
            data.surfaceWaiverHolds = [...formatted];
        }

        return data;
    };

    const finalizeSurfaceWaiverHold = (data: SurfaceWaiverHold, grossAcres: number) => {
        data.id = data.id ? parseInt(data.id + '', 10) : 0;
        data.surfaceWaiverId = surfaceWaiverData?.id ? surfaceWaiverData.id : 0;

        data.primaryTerm = data.primaryTerm ? parseInt(data.primaryTerm + '', 10) : null;
        data.primaryTermTypeId = data.primaryTermTypeId ? parseInt(data.primaryTermTypeId + '', 10) : null;
        data.bonusPaidTypeId = data.bonusPaidTypeId ? parseInt(data.bonusPaidTypeId + '', 10) : null;

        data.additionalConsideration = data.additionalConsideration ? parseFloat(data.additionalConsideration + '') : 0;
        data.decimalInterest = data.decimalInterest ? parseFloat(data.decimalInterest + '') : 0;
        data.bonusPaid = data.bonusPaid ? parseFloat(data.bonusPaid + '') : 0;
        data.totalBonusPaid = data.totalBonusPaid ? parseFloat(data.totalBonusPaid + '') : 0;

        data.note = data.note ? data.note : '';

        data.waiverDate = data.waiverDate
            ? (moment(data.waiverDate).format('YYYY-MM-DD') as unknown as Date)
            : (moment().format('YYYY-MM-DD') as unknown as Date);
        data.expirationDate = data.expirationDate ? (moment(data.expirationDate).format('YYYY-MM-DD') as unknown as Date) : null;

        data.hbp = AgreementUtil.parseBool(data.hbp + '');
        data.hbu = AgreementUtil.parseBool(data.hbu + '');

        if (data.contractOwnerships?.length > 0) {
            let formatted = new Array<WIContractOwnership>();
            data.contractOwnerships.forEach((owner: WIContractOwnership) => {
                formatted.push(finalizeWIOwnership(owner));
            });
            data.contractOwnerships = [...formatted];
        }

        return data;
    };

    const finalizeWIOwnership = (data: WIContractOwnership) => {
        data.id = data.id ? parseInt(data.id + '', 10) : 0;
        data.projectId = data.projectId ? parseInt(data.projectId + '', 10) : 0;
        data.ownerLookUpId = data.ownerLookUpId ? parseInt(data.ownerLookUpId + '', 10) : 0;
        data.parentOwnershipId = data.parentOwnershipId ? parseInt(data.parentOwnershipId + '', 10) : 0;
        data.ownershipId = data.ownershipId ? parseInt(data.ownershipId + '', 10) : 0;
        data.contractLookupId = data.contractLookupId ? parseInt(data.contractLookupId + '', 10) : 0;

        data.decimalInterest = data.decimalInterest ? parseFloat(data.decimalInterest + '') : 0;

        data.note = data.note ? data.note : '';

        if (data.orriOwnerships?.length > 0) {
            let formatted = new Array<WIContractOwnership>();
            data.orriOwnerships.forEach((orri: WIContractOwnership) => {
                formatted.push(finalizeWIOwnership(orri));
            });
            data.orriOwnerships = [...formatted];
        }

        return data;
    };

    const getOptions = (type: string, item: string = '') => {
        if (measurementTypes?.length > 0) {
            return measurementTypes
                .filter((mt) => mt.description === type)
                .map((option) => {
                    if (type === 'Fee' && option.name === 'Per Item') {
                        return { value: option.id, label: `Per ${item}` };
                    }
                    return { value: option.id, label: option.name };
                });
        }
        return [];
    };

    const getUsers = async () => {
        const result = await get('v1.0/users/user-list');

        if (response.ok) {
            setUsers(result);
        }
    };

    const previousContractHold = () => {
        if (currentWaiverHold > 0) {
            const surfaceWaiverHoldId = surfaceWaiverData?.surfaceWaiverHolds?.[currentWaiverHold - 1]?.id;
            setCurrentWaiverHold(currentWaiverHold - 1);
            dispatch(
                push(
                    `/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/sections/${appContext.fieldNumber}/surface-tracts/${appContext.surfaceTractNumber}/ownership/${appContext.ownershipId}/surface-waivers/${appContext.surfaceWaiverId}/surface-waiver-holds/${surfaceWaiverHoldId}`
                )
            );
        }
    };

    const nextContractHold = () => {
        if (currentWaiverHold + 1 < surfaceWaiverData?.surfaceWaiverHolds?.length) {
            const surfaceWaiverHoldId = surfaceWaiverData?.surfaceWaiverHolds?.[currentWaiverHold + 1]?.id;

            setCurrentWaiverHold(currentWaiverHold + 1);
            dispatch(
                push(
                    `/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/sections/${appContext.fieldNumber}/surface-tracts/${appContext.surfaceTractNumber}/ownership/${appContext.ownershipId}/surface-waivers/${appContext.surfaceWaiverId}/surface-waiver-holds/${surfaceWaiverHoldId}`
                )
            );
        }
    };

    const handleExpirationChange = () => {
        calcExpirationDate();
    };

    const onUpdateWINote = async (ownership: Ownership) => {
        await put(
            `/v1.0/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/fields/${appContext.sectionNumber}/surfaceTracts/notes`,
            ownership
        );

        if (response.ok) {
            dispatch(actions.layout.hideDialog());
            getSurfaceWaiverData(appContext.surfaceWaiverId);
        } else {
            window.alert(`update wi note failed. note: ${ownership.note}`);
        }
    };

    const calcExpirationDate = () => {
        const term = watchExpirationCalcFields['currentHold.primaryTerm']
            ? parseInt(watchExpirationCalcFields['currentHold.primaryTerm'], 10)
            : undefined;
        const termUnit = watchExpirationCalcFields['currentHold.primaryTermTypeId']
            ? watchExpirationCalcFields['currentHold.primaryTermTypeId']
            : undefined;
        const effectiveDate = watchExpirationCalcFields.effectiveDate
            ? new Date(watchExpirationCalcFields.effectiveDate.toString()) || undefined
            : undefined;

        if (term && termUnit && effectiveDate && !isNaN(term) && termUnit && effectiveDate instanceof Date) {
            let termString = measurementTypes.find((mt) => mt.id === parseInt(termUnit, 10))?.name;
            switch (termString) {
                case 'Years':
                    setValue(
                        'currentHold.expirationDate',
                        new Date(effectiveDate.setFullYear(effectiveDate.getFullYear() + term)).toISOString().split('T')[0]
                    );
                    break;

                case 'Months':
                    setValue(
                        'currentHold.expirationDate',
                        new Date(effectiveDate.setMonth(effectiveDate.getMonth() + term)).toISOString().split('T')[0]
                    );
                    break;

                case 'Days':
                    setValue(
                        'currentHold.expirationDate',
                        new Date(effectiveDate.setDate(effectiveDate.getDate() + term)).toISOString().split('T')[0]
                    );
                    break;

                default:
                    break;
            }
        } else {
            setValue('currentHold.expirationDate', '');
        }
    };

    const onUpdatePerpetual = () => {
        let ref = getValues();
        if (!!ref.currentHold) {
            setIsPerpetual(ref.currentHold.isPerpetual === true);
        }
    };

    const onTryAddWI = () => {
        if (!isNaN(surfaceWaiverData?.surfaceWaiverHolds?.[currentWaiverHold]?.id)) {
            dialogContext.contract.addContractOwnership(
                DialogList.ADD_WI_OWNERSHIP,
                'Add Working Interest Ownership',
                undefined,
                undefined,
                appContext.clientNumber,
                appContext.projectNumber,
                AddOwnershipPages.AddOwnershipLandingPage,
                (path: string) => dispatch(push(path)),
                null,
                onSaveAddOwnership
            );
        } else {
            dispatch(actions.layout.showDialog(DialogList.LEASEHOLD_SAVE_BEFORE_ADDING_WI));
        }
    };

    const onAddORRI = (newParentOwnershipId: number) => {
        dialogContext.contract.addContractOwnership(
            DialogList.ADD_ORRI_OWNERSHIP,
            'Add Overriding Royalty Interest Ownership',
            undefined,
            newParentOwnershipId,
            appContext.clientNumber,
            appContext.projectNumber,
            AddOwnershipPages.AddOwnershipLandingPage,
            (path: string) => dispatch(push(path)),
            null,
            onSaveAddOwnership
        );
    };

    const onCopyORRI = (ownershipId: number) => {
        dialogContext.leasehold.cloneORRI(ownershipId, ownerNameForWorkingInterest(ownershipId), () => location.reload());
    };

    const ownerNameForWorkingInterest = (workingInterestId: number) => {
        const wiOwners = PropertyUtil.SafeGet(() => surfaceWaiverData?.surfaceWaiverHolds?.[currentWaiverHold]?.wiOwners);
        const wiOwnerships = PropertyUtil.SafeGet(() => surfaceWaiverData?.surfaceWaiverHolds?.[currentWaiverHold]?.contractOwnerships);
        const ownership = wiOwnerships?.filter((wio: Ownership) => wio.id === workingInterestId)[0];
        const owner = wiOwners.filter((o: Owner) => o.ownerLookUpId === ownership.ownerLookUpId)[0];

        return owner?.name || '[Error]';
    };

    const onDeleteORRI = (ownershipId: number) => {
        const wiOwners = PropertyUtil.SafeGet(() => surfaceWaiverData.surfaceWaiverHolds?.[currentWaiverHold]?.wiOwners);
        const wiOwnerships = PropertyUtil.SafeGet(() => surfaceWaiverData.surfaceWaiverHolds?.[currentWaiverHold]?.contractOwnerships);
        const ownership: WIOwnership = wiOwnerships?.filter((wio: WIOwnership) => wio.id === ownershipId)[0];

        dialogContext.leasehold.deleteORRI(ownershipId, ownerNameForWorkingInterest(ownershipId), ownership.orriOwnerships, wiOwners, () =>
            location.reload()
        );
    };

    const onDeleteContractOwnership = async (contractOwnershipId: number) => {
        await del(
            `/v1.0/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/contract-ownership/${contractOwnershipId}`
        );
        if (response.ok) {
            dispatch(actions.layout.hideDialog());
            getSurfaceWaiverData(appContext?.surfaceWaiverId);
        } else {
            window.alert('Get Surface Waiver Data failed');
        }
    };

    const openOwnershipNotes = (ownershipId: number) => {
        // this.setState({ ownershipNotesId: ownershipId });
        setSelectedWIOwnershipId(ownershipId);
        dialogContext.tract.openOwnershipNotes(
            findOwnership(ownershipId),
            surfaceWaiverData?.surfaceWaiverHolds?.[currentWaiverHold]?.wiOwners,
            '',
            2,
            (ownership: Ownership) => onUpdateWINote(ownership)
        );
    };

    const onSaveAddOwnership = async (values: AddWIOwnershipData) => {
        values.contractOwnership.contractLookupId = surfaceWaiverData.surfaceWaiverHolds[currentWaiverHold].contractLookupId;

        await post(
            `/v1.0/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/contract-ownership/${LandContractType.SurfaceWaiver}`,
            values
        );

        if (response.ok) {
            dispatch(
                push(
                    `/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/sections/${appContext.sectionNumber}/surface-tracts/${appContext.surfaceTractNumber}/ownership/${appContext.ownershipId}/surface-waivers/${appContext.surfaceWaiverId}/surface-waiver-holds/1`
                )
            );

            getSurfaceWaiverData(appContext.surfaceWaiverId);
        } else {
            window.alert('Update Contract Ownership failed');
        }
    };

    const findOwnership = (ownershipId: number = 0) => {
        let refId = ownershipId > 0 ? ownershipId : selectedWIOwnershipId;
        let ownership = surfaceWaiverData.surfaceWaiverHolds[currentWaiverHold].contractOwnerships?.find(
            (lo: WIContractOwnership) => lo.id === refId
        );

        if (!ownership) {
            surfaceWaiverData.surfaceWaiverHolds[currentWaiverHold].contractOwnerships?.forEach((lo: WIContractOwnership) => {
                let tempOwnership = lo.orriOwnerships.find((wio: WIContractOwnership) => wio.id === refId);
                if (tempOwnership) {
                    ownership = tempOwnership;
                }
                console.log('f');
            });
        }

        return ownership;
    };

    const warningStyle: React.CSSProperties = {
        display: 'inline-block',
        padding: '0.5rem',
        margin: '0 0 1rem 0',
        fontWeight: 700,
        background: '#d5d5d5',
        fontSize: '.8em',
        color: '#565656',
        width: '100%',
        textAlign: 'center',
        borderRadius: '0.4rem',
    };

    return (
        <div className="lease-info-page">
            <div>
                <form onSubmit={handleSubmit((data) => onSubmit(data as unknown as SurfaceWaiver))}>
                    <div>
                        {isDirty && (
                            <>
                                <div
                                    id="dirtyFlag"
                                    style={{
                                        position: 'fixed',
                                        top: '4.7rem',
                                        left: '0',
                                        width: '100%',
                                        color: 'white',
                                        backgroundColor: 'rgba(255,0,0,.5)',
                                        zIndex: 9000,
                                        padding: '1rem 0',
                                        display: 'flex',
                                        justifyContent: 'center',
                                    }}
                                >
                                    <div>Unsaved Changes - Please Save Before Leaving Form</div>
                                    <div style={{ position: 'absolute', right: '.5rem', top: '50%', transform: 'translateY(-50%)', display: 'flex' }}>
                                        <div>
                                            <Button text="Save" type="submit" size="small" intent="primary" />
                                        </div>
                                        <div style={{ margin: '0 1rem', borderRight: '1px solid white', width: '1px' }} />
                                        <Button text="Cancel Changes" size="small" onClick={() => reset()} />
                                    </div>
                                </div>
                            </>
                        )}
                    </div>

                    {/* Added to let the user know fields with * are required */}
                    <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '0.5rem', marginRight: '1rem' }}>
                        <span style={{ color: 'red', transform: 'scale(2)' }}>*</span>
                        <p>= Fields are required!</p>
                    </div>

                    <RHFLegend title="Surface Waiver" />
                    <div hidden={true}>
                        <RHFInputBox label="" name="id" register={register} type="number" disabled={true} errors={errors} />
                        <RHFCheckbox name="isGeneratedLease" label="" disabled={true} register={register} errors={errors} />
                    </div>
                    <div className="form-row" style={{ width: '60%' }}>
                        <RHFInputBox
                            label="Surface Waiver Name"
                            name="name"
                            style={{ width: '100%' }}
                            register={register}
                            errors={errors}
                            isRequired={true}
                        />
                    </div>
                    <div className="form-row">
                        <RHFInputBox
                            label="DLC Number"
                            name="surfaceWaiverNumber"
                            style={{ width: '100%' }}
                            register={register}
                            errors={errors}
                            disabled={true}
                        />
                        <RHFInputBox
                            label="Contract Date"
                            name="contractDate"
                            register={register}
                            type="date"
                            errors={errors}
                            rules={{ required: 'field required' }}
                            isRequired={true}
                        />

                        <RHFInputBox
                            label="Effective Date"
                            name="effectiveDate"
                            register={register}
                            type="date"
                            errors={errors}
                            rules={{ required: 'field required' }}
                            isRequired={true}
                        />
                    </div>
                    <div className="form-row">
                        <RHFSelect
                            name="recordTypeId"
                            label="Record Type"
                            options={[
                                { value: 1, label: 'Book/Volume' },
                                { value: 2, label: 'Instrument #' },
                            ]}
                            register={register}
                            errors={errors}
                        />
                        <RHFInputBox label="Volume" name="volume" register={register} type="text" errors={errors} />
                        <RHFInputBox label="Page" name="page" register={register} type="text" errors={errors} />
                        <RHFInputBox label="Inst. Number" name="instrumentNumber" register={register} type="text" errors={errors} />
                        <RHFInputBox
                            label="Recorded Date"
                            name="recordedDate"
                            register={register}
                            type="date"
                            errors={errors}
                        />
                        <RHFInputBox label="Fed/State Lease #" name="fedStateLeaseNumber" register={register} type="number" errors={errors} />
                        <RHFInputBox label="Recording Date" name="recordDate" register={register} type="date" errors={errors} />
                        <RHFCheckbox name="isFiledByMemo" label="Filed By Memorandum" register={register} errors={errors} />
                    </div>

                    <div className="form-row">
                        <RHFInputBox
                            label="Original Gross Acres"
                            name="grossAcres"
                            style={{ width: '100%' }}
                            register={register}
                            type="number"
                            errors={errors}
                            rules={{ required: 'field required' }}
                            isRequired={true}
                            step={0.0001}
                        />
                        <RHFInputBox
                            label="Original Grantor"
                            name="ownersOnDocument"
                            style={{ width: '100%' }}
                            register={register}
                            errors={errors}
                            rules={{ required: 'field required' }}
                            isRequired={true}
                        />
                        <RHFInputBox label="Original Grantee" name="lesseeName" style={{ width: '100%' }} register={register} errors={errors} />
                        <RHFInputBox label="Grantor AKA" name="grantorAka" style={{ width: '100%' }} register={register} errors={errors} />
                        <RHFInputBox label="Client Doc Number" name="clientDocNumber" register={register} type="text" errors={errors} />
                    </div>

                    {/* TODO: Set this up for multiple Surface Waiver Holds. */}
                    <RHFLegend title="Surface Waiver Hold" />
                    <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}>
                        <div className="form-row">
                            <Button text="Previous" onClick={previousContractHold} size="small" intent="primary" />
                            {/* <span onClick={previousLeasehold} style={{ border: '1px solid black' }} className="clickable">Previous</span> */}
                            <span style={{ padding: '.5rem' }}>{`${currentWaiverHold + 1} of ${
                                surfaceWaiverData?.surfaceWaiverHolds?.length || 1
                            }`}</span>
                            <Button text="Next" onClick={nextContractHold} size="small" intent="primary" />
                            {/* <span onClick={nextLeasehold} style={{ border: '1px solid black' }} className="clickable">Next</span> */}
                            {surfaceWaiverData?.surfaceWaiverHolds?.[currentWaiverHold]?.surfaceOwner && (
                                <div style={{ marginLeft: '2rem', fontSize: '1.5rem' }}>
                                    <span>Owner: </span>
                                    <span>
                                        {OwnerUtil.MapOwner(surfaceWaiverData?.surfaceWaiverHolds?.[currentWaiverHold]?.surfaceOwner)?.ownerName() ||
                                            'N/A'}
                                    </span>
                                </div>
                            )}
                        </div>
                        {surfaceWaiverData?.surfaceWaiverHolds?.length > 1 && (
                            <div style={{ display: 'flex' }}>
                                {copyLeaseholdClicked && (
                                    <div style={{textAlign: 'center', marginRight: '1rem'}}>
                                        <RHFSelect 
                                            onChange={(e) => handleSourceSelected(e)} 
                                            name="sourceLeaseholdId" 
                                            label="Source Leasehold" 
                                            options={leaseholdList()} 
                                            register={register} 
                                        />
                                        <Button 
                                            style={{marginTop: '1rem'}}
                                            disabled={!sourceSelected} 
                                            text="Confirm Copy"
                                            intent="danger" 
                                            onClick={onCopyLeasehold} 
                                        />
                                    </div>
                                )}
                                <div>
                                    {appContext.surfaceWaiverHoldId === 0 && (
                                        <Button
                                            style={{ marginLeft: '2rem' }}
                                            text="Copy Leasehold"
                                            onClick={() => setCopyLeaseholdClicked(true)}
                                            intent="primary" 
                                        />
                                    )}
                                    <Button
                                        style={{ marginLeft: '2rem' }}
                                        text="Delete Leasehold"
                                        onClick={() => deleteSurfaceWaiverhold(false)}
                                        intent="danger"
                                    />
                                </div>
                            </div>
                        )}
                        {surfaceWaiverData?.surfaceWaiverHolds?.length === 1 && (
                            <div style={{ display: 'flex' }}>
                                <div>
                                    <Button
                                        style={{ marginLeft: '2rem' }}
                                        text="Delete Waiver"
                                        onClick={() => deleteSurfaceWaiverhold(true)}
                                        intent="danger"
                                    />
                                </div>
                            </div>                        
                        )}
                    </div>

                    <div hidden={true}>
                        <RHFInputBox label="" name="currentHold.id" register={register} type="number" disabled={true} errors={errors} />
                        <RHFInputBox label="" name="currentHold.surfaceWaiverId" register={register} type="number" disabled={true} errors={errors} />
                    </div>

                    <div className="form-row">
                        <RHFCheckbox
                            name="currentHold.isPerpetual"
                            label="Perpetual Leasehold?"
                            register={register}
                            errors={errors}
                            onClick={() => onUpdatePerpetual()}
                        />
                    </div>
                    <div className="form-row">
                        <RHFCheckbox 
                            style={{marginBottom: '2rem'}}
                            label={'Active Lease'} 
                            name={'currentHold.isActive'} 
                            register={register} 
                            errors={errors} 
                        />
                    </div>
                    {!isPerpetual && (
                        <div className="form-row">
                            <RHFInputBox
                                label="Primary Term"
                                name="currentHold.primaryTerm"
                                register={register}
                                type="number"
                                errors={errors}
                                onChange={handleExpirationChange}
                            />
                            <RHFSelect
                                name="currentHold.primaryTermTypeId"
                                label="Term Unit"
                                options={getOptions('Time')}
                                onChange={handleExpirationChange}
                                register={register}
                                errors={errors}
                            />
                            <RHFInputBox label="Exp. Date" name="currentHold.expirationDate" register={register} type="date" errors={errors} />
                        </div>
                    )}

                    <div className="form-row">
                        <RHFInputBox label="Decimal Interest" name="currentHold.decimalInterest" register={register} type="number" errors={errors} step={0.0001} />
                        <RHFInputBox
                            label="Bonus Paid"
                            name="currentHold.bonusPaid"
                            register={register}
                            // onChange={handleTotalBonusPaid}
                            type="number"
                            errors={errors}
                            step={0.0001}
                        />
                        <RHFSelect
                            name="currentHold.bonusPaidTypeId"
                            label="Bonus Unit"
                            options={getOptions('Fee', 'acre')}
                            // onChange={handleTotalBonusPaid}
                            register={register}
                            errors={errors}
                        />
                        <RHFInputBox
                            label="Total Bonus Paid"
                            name="currentHold.totalBonusPaid"
                            register={register}
                            type="number"
                            errors={errors}
                            step={0.0001}
                        />
                    </div>

                    <div className="form-row">
                        <RHFInputBox
                            label="Additional Consideration"
                            name="currentHold.additionalConsideration"
                            register={register}
                            type="number"
                            errors={errors}
                            bodyStyle={{ width: '20%' }}
                            style={{ width: '100%' }}
                            step={0.0001}
                        />
                        <RHFInputBox
                            label="Description"
                            name="currentHold.description"
                            register={register}
                            errors={errors}
                            bodyStyle={{ width: '80%' }}
                            style={{ width: '100%' }}
                        />
                    </div>

                    <RHFLegend title="Original Legal Description" />
                    <div className="form-row">
                        {/* <div className="form-field" style={{ width: '100%' }}>
                            <textarea
                                name="legalDescription"
                                ref={register}
                                style={{
                                    width: '100%',
                                    height: '6rem',
                                    border: '0',
                                    borderBottom: '1px solid black',
                                    resize: 'none',
                                    overflowY: 'auto',
                                }}
                            />
                        </div> */}
                        <RHFTextArea
                            label={''}
                            name={'legalDescription'}
                            style={{
                                width: '100%',
                                height: '6rem',
                                border: '0',
                                borderBottom: '1px solid black',
                                resize: 'none',
                                overflowY: 'auto',
                            }}
                            register={register}
                        />
                    </div>

                    <RHFLegend title="Contract Notes" />
                    <div className="form-row" style={{ width: '100%' }}>
                        {/* <div className="form-field" style={{ width: '100%' }}>
                            <textarea
                                name="leaseLessorNotes"
                                ref={register}
                                style={{
                                    width: '100%',
                                    height: '6rem',
                                    border: '0',
                                    borderBottom: '1px solid black',
                                    resize: 'none',
                                    overflowY: 'auto',
                                }}
                            />
                        </div> */}
                        <RHFTextArea
                            label={''}
                            name={'leaseLessorNotes'}
                            style={{
                                width: '100%',
                                height: '6rem',
                                border: '0',
                                borderBottom: '1px solid black',
                                resize: 'none',
                                overflowY: 'auto',
                            }}
                            register={register}
                        />
                    </div>
                          
                    <RHFLegend title="Pooling Information" />

                    <div className="form-row">
                        <RHFToggle label="HBP" name="currentHold.hbp" register={register} errors={errors} />
                        <RHFInputBox
                            label="Pooling Order Number"
                            name="currentHold.poolingOrderNumber"
                            register={register}
                            type="text"
                            errors={errors}
                        />
                        <RHFInputBox label="Operator" name="currentHold.operator" register={register} type="text" errors={errors} />
                        <RHFInputBox label="Well Name" name="currentHold.wellName" register={register} type="text" errors={errors} />
                        <RHFToggle label="HBU" name="currentHold.hbu" register={register} errors={errors} />
                        <RHFInputBox label="API #" name="currentHold.apiNumber" register={register} type="text" errors={errors} />
                        <RHFInputBox label="Unit Name" name="currentHold.unitName" register={register} type="text" errors={errors} />
                        <RHFInputBox label="Field Name" name="currentHold.fieldName" register={register} type="text" errors={errors} />
                    </div>

                    <RHFLegend title="Provisions" />
                    {attachedProvisions && (
                        <div style={{ margin: 'auto 2rem' }}>
                            <ProvisionClause
                                attachedProvisionClauses={attachedProvisions}
                                options={provisionClauseTypes}
                                formPrefix="currentHold.provisionLeaseClauses"
                                register={register}
                                errors={errors}
                                getValues={getValues}
                                setValue={setValue}
                                key="provisionClause"
                            />
                        </div>
                    )}

                    <RHFLegend title="Document Management" />
                    {surfaceWaiverData?.surfaceWaiverHolds && (
                        <ContractHoldDocs
                            showDialog={(dialogName: string) => dispatch(actions.layout.showDialog(dialogName))}
                            hideDialog={() => dispatch(actions.layout.hideDialog())}
                            contractLookupId={surfaceWaiverData?.surfaceWaiverHolds?.[currentWaiverHold]?.contractLookupId}
                            contractType={'Contract Hold'}
                        />
                    )}

                    <RHFLegend title="Contract Ownership" />
                    <span style={warningStyle}>ALL INTEREST REPRESENTED AS DECIMAL</span>
                    <div style={{ minHeight: '20rem' }}>
                        <ContractWorkingInterestList
                            isGovStateBlm={false}
                            owners={surfaceWaiverData?.surfaceWaiverHolds?.[currentWaiverHold]?.wiOwners}
                            wiContractOwnerships={surfaceWaiverData?.surfaceWaiverHolds?.[currentWaiverHold]?.contractOwnerships}
                            prefix={'currentHold'}
                            setDialogShowing={(dialogName: string) => dispatch(actions.layout.showDialog(dialogName))}
                            tryAddWI={onTryAddWI}
                            onAddORRI={onAddORRI}
                            onCopyORRI={onCopyORRI}
                            onDeleteORRI={onDeleteORRI}
                            deleteContractOwnership={onDeleteContractOwnership}
                            openOwnershipNotes={openOwnershipNotes}
                            register={register}
                            errors={errors}
                            getValues={getValues}
                            setValue={setValue}
                            watch={watch}
                            royalty={royalty}
                        />
                    </div>

                    <div>
                        <Button text="Save" type="submit" intent="primary" />
                    </div>
                </form>
            </div>
        </div>
    );
};

export default SurfaceWaiverInfo;



// WEBPACK FOOTER //
// ./src/components/project/sections/TractComponents/LeaseholdInfoComponents/SurfaceWaiverInfo.tsx